import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FaCalendarAlt, FaFilter, FaFileExcel, FaSpinner, FaChartLine, FaPrint, FaChartBar, FaSortAmountDown, FaSortAmountUp, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { newFinanceService } from '../../services/newFinance.service';
import type { FinanceRecord, SearchableOfferingType, ExpenseCategory, CurrencyTotals, OtherIncomeCategory } from '../../types/newFinance';
import { OFFERING_TYPE_LABELS, EXPENSE_CATEGORY_LABELS, OTHER_INCOME_CATEGORY_LABELS } from '../../types/newFinance';
import { useNotification } from '../../contexts/NotificationContext';
import { exportFinanceRecordsToCsv } from '../../utils/exportUtils';
import { AnnualFinanceCharts } from './AnnualFinanceCharts';
import { usePagination } from '../../hooks/usePagination';

export const AnnualFinanceView: React.FC = () => {
  const { addNotification } = useNotification();
  const printRef = useRef<HTMLDivElement>(null);

  // 상태 관리
  const [records, setRecords] = useState<FinanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCharts, setShowCharts] = useState(false);

  // 필터 상태 (변경됨!)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number | 'all'>('all');
  const [selectedIncomeType, setSelectedIncomeType] = useState<SearchableOfferingType | 'all'>('all');
  const [selectedExpenseType, setSelectedExpenseType] = useState<ExpenseCategory | 'all'>('all');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

  // 사용 가능한 연도 목록
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let i = currentYear; i >= currentYear - 5; i--) {
      years.push(i);
    }
    return years;
  }, []);

  // 데이터 로드
  useEffect(() => {
    loadAnnualData();
  }, [selectedYear]);

  const loadAnnualData = async () => {
    setLoading(true);
    try {
      const allRecords = await newFinanceService.getAllFinanceRecords();
      // 선택된 연도의 승인된 데이터만 필터링
      const yearRecords = allRecords.filter(record => {
        const recordYear = new Date(record.date).getFullYear();
        return recordYear === selectedYear && record.status === 'approved';
      });
      setRecords(yearRecords);
    } catch (error) {
      console.error('연간 데이터 로딩 오류:', error);
      addNotification('연간 데이터를 불러올 수 없습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 필터링 및 정렬된 데이터
  const filteredAndSortedRecords = useMemo(() => {
    let filtered = records.filter(record => {
      // 월 필터
      if (selectedMonth !== 'all') {
        const recordMonth = new Date(record.date).getMonth() + 1;
        if (recordMonth !== selectedMonth) return false;
      }

      // 수입 필터
      if (selectedIncomeType !== 'all') {
        let hasIncomeType = false;

        if (selectedIncomeType === 'tithe') {
          hasIncomeType = record.titheItems.length > 0;
        } else if (selectedIncomeType === 'thanksgiving') {
          hasIncomeType = record.thanksgivingItems.length > 0;
        } else {
          // 기타 헌금 종류
          hasIncomeType = record.otherIncomeItems.some(item =>
            item.category === selectedIncomeType
          );
        }

        if (!hasIncomeType) return false;
      }

      // 지출 필터
      if (selectedExpenseType !== 'all') {
        const hasExpenseType = record.expenseItems.some(item =>
          item.category === selectedExpenseType
        );
        if (!hasExpenseType) return false;
      }

      return true;
    });

    // 정렬
    filtered.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  }, [records, selectedMonth, selectedIncomeType, selectedExpenseType, sortOrder]);

  // 페이지네이션
  const pagination = usePagination(filteredAndSortedRecords, {
    initialPageSize: 10,
    pageSizeOptions: [10, 20, 50, 100]
  });

  // 필터 조건에 맞는 합계 계산 헬퍼
  const calculateFilteredTotals = (record: FinanceRecord) => {
    const income: CurrencyTotals = { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 };
    const expense: CurrencyTotals = { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 };

    // 수입 계산
    if (selectedIncomeType === 'all') {
      Object.entries(record.incomeTotals).forEach(([currency, amount]) => {
        income[currency as keyof CurrencyTotals] += amount;
      });
    } else {
      if (selectedIncomeType === 'tithe') {
        record.titheItems.forEach(item => income[item.currency as keyof CurrencyTotals] += item.amount);
      } else if (selectedIncomeType === 'thanksgiving') {
        record.thanksgivingItems.forEach(item => income[item.currency as keyof CurrencyTotals] += item.amount);
      } else {
        record.otherIncomeItems
          .filter(item => item.category === selectedIncomeType)
          .forEach(item => income[item.currency as keyof CurrencyTotals] += item.amount);
      }
    }

    // 지출 계산
    if (selectedExpenseType === 'all') {
      Object.entries(record.expenseTotals).forEach(([currency, amount]) => {
        expense[currency as keyof CurrencyTotals] += amount;
      });
    } else {
      record.expenseItems
        .filter(item => item.category === selectedExpenseType)
        .forEach(item => expense[item.currency as keyof CurrencyTotals] += item.amount);
    }

    const net: CurrencyTotals = {
      HUF: income.HUF - expense.HUF,
      EUR: income.EUR - expense.EUR,
      USD: income.USD - expense.USD,
      KRW: income.KRW - expense.KRW,
      GBP: income.GBP - expense.GBP,
      CAD: income.CAD - expense.CAD
    };

    return { income, expense, net };
  };

  // 통계 계산 (필터링된 전체 데이터 기준)
  const statistics = useMemo(() => {
    const incomeTotals: CurrencyTotals = { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 };
    const expenseTotals: CurrencyTotals = { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 };

    filteredAndSortedRecords.forEach(record => {
      const { income, expense } = calculateFilteredTotals(record);

      Object.entries(income).forEach(([currency, amount]) => {
        incomeTotals[currency as keyof CurrencyTotals] += amount;
      });

      Object.entries(expense).forEach(([currency, amount]) => {
        expenseTotals[currency as keyof CurrencyTotals] += amount;
      });
    });

    const netTotals: CurrencyTotals = {
      HUF: incomeTotals.HUF - expenseTotals.HUF,
      EUR: incomeTotals.EUR - expenseTotals.EUR,
      USD: incomeTotals.USD - expenseTotals.USD,
      KRW: incomeTotals.KRW - expenseTotals.KRW,
      GBP: incomeTotals.GBP - expenseTotals.GBP,
      CAD: incomeTotals.CAD - expenseTotals.CAD
    };

    return { incomeTotals, expenseTotals, netTotals };
  }, [filteredAndSortedRecords, selectedIncomeType, selectedExpenseType]);

  // 통화별 금액 표시 헬퍼
  const formatCurrencyAmount = (totals: CurrencyTotals): string[] => {
    return Object.entries(totals)
      .filter(([_, amount]) => amount !== 0)
      .map(([currency, amount]) => `${amount.toLocaleString()} ${currency}`);
  };

  // 필터 초기화
  const resetFilters = () => {
    setSelectedMonth('all');
    setSelectedIncomeType('all');
    setSelectedExpenseType('all');
    setSortOrder('desc');
    pagination.setCurrentPage(1);
  };

  // 빠른 날짜 필터
  const setQuickDateFilter = (type: 'current' | 'last' | 'recent3') => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;

    if (type === 'current') {
      setSelectedMonth(currentMonth as number);
    } else if (type === 'last') {
      const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
      setSelectedMonth(lastMonth as number);
    } else if (type === 'recent3') {
      // 최근 3개월은 'all'로 두고 추가 필터링이 필요할 수 있음
      setSelectedMonth('all');
      addNotification('최근 3개월 필터는 "전체" 보기로 설정됩니다.', 'info');
    }
    pagination.setCurrentPage(1);
  };

  // 엑셀 내보내기
  const exportToExcel = () => {
    if (filteredAndSortedRecords.length === 0) {
      addNotification('내보낼 데이터가 없습니다.', 'warning');
      return;
    }

    try {
      // 필터 정보를 파일명에 포함
      let filename = `${selectedYear}년_재정내역`;
      if (selectedMonth !== 'all') {
        filename += `_${selectedMonth}월`;
      }
      if (selectedIncomeType !== 'all') {
        const label = selectedIncomeType === 'tithe' ? '십일조' :
          selectedIncomeType === 'thanksgiving' ? '감사헌금' :
            OTHER_INCOME_CATEGORY_LABELS[selectedIncomeType as OtherIncomeCategory];
        filename += `_${label}`;
      }
      if (selectedExpenseType !== 'all') {
        filename += `_${EXPENSE_CATEGORY_LABELS[selectedExpenseType]}`;
      }

      exportFinanceRecordsToCsv(filteredAndSortedRecords, filename);
      addNotification(`${filteredAndSortedRecords.length}건의 재정 데이터를 엑셀로 내보냈습니다.`, 'success');
    } catch (error) {
      console.error('엑셀 내보내기 오류:', error);
      addNotification('엑셀 내보내기 중 오류가 발생했습니다.', 'error');
    }
  };

  return (
    <div ref={printRef} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
      {/* 헤더 */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <FaChartLine className="h-5 w-5 mr-2 text-purple-500" />
              📊 연간 입출금 내역
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              연간 재정 데이터를 조회하고 다양한 필터로 분석하세요
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowCharts(!showCharts)}
              className={`px-4 py-2 ${showCharts ? 'bg-purple-600' : 'bg-purple-500'} hover:bg-purple-600 text-white rounded-lg transition-colors flex items-center space-x-2`}
            >
              <FaChartBar className="h-4 w-4" />
              <span>{showCharts ? '차트 숨기기' : '차트 보기'}</span>
            </button>
            <button
              onClick={() => window.print()}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center space-x-2"
            >
              <FaPrint className="h-4 w-4" />
              <span>인쇄</span>
            </button>
            <button
              onClick={exportToExcel}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center space-x-2"
            >
              <FaFileExcel className="h-4 w-4" />
              <span>엑셀 다운로드</span>
            </button>
          </div>
        </div>

        {/* 필터 영역 */}
        <div className="space-y-4">
          {/* 기본 필터 첫 줄 */}
          <div className="flex flex-wrap gap-3">
            {/* 연도 선택 */}
            <div className="flex items-center space-x-2">
              <FaCalendarAlt className="h-4 w-4 text-gray-400" />
              <select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(Number(e.target.value));
                  pagination.setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}년</option>
                ))}
              </select>
            </div>

            {/* 월 선택 */}
            <select
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value === 'all' ? 'all' : Number(e.target.value));
                pagination.setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="all">전체 월</option>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                <option key={month} value={month}>{month}월</option>
              ))}
            </select>

            {/* 빠른 날짜 필터 버튼들 */}
            <button
              onClick={() => setQuickDateFilter('current')}
              className="px-3 py-2 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 rounded-lg transition-colors text-sm"
            >
              이번 달
            </button>
            <button
              onClick={() => setQuickDateFilter('last')}
              className="px-3 py-2 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 rounded-lg transition-colors text-sm"
            >
              지난 달
            </button>

            {/* 초기화 버튼 */}
            <button
              onClick={resetFilters}
              className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              필터 초기화
            </button>
          </div>

          {/* 수입/지출 드롭다운 필터 */}
          <div className="flex flex-wrap gap-3">
            {/* 수입 드롭다운 */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">수입:</span>
              <select
                value={selectedIncomeType}
                onChange={(e) => {
                  setSelectedIncomeType(e.target.value as SearchableOfferingType | 'all');
                  pagination.setCurrentPage(1);
                }}
                className="px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white bg-blue-50 dark:bg-blue-900/20"
              >
                <option value="all">전체</option>
                <option value="tithe">십일조</option>
                <option value="thanksgiving">감사헌금</option>
                {Object.entries(OTHER_INCOME_CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            {/* 지출 드롭다운 */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">지출:</span>
              <select
                value={selectedExpenseType}
                onChange={(e) => {
                  setSelectedExpenseType(e.target.value as ExpenseCategory | 'all');
                  pagination.setCurrentPage(1);
                }}
                className="px-3 py-2 border border-orange-300 dark:border-orange-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-white bg-orange-50 dark:bg-orange-900/20"
              >
                <option value="all">전체</option>
                {Object.entries(EXPENSE_CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            {/* 정렬 버튼 */}
            <button
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors flex items-center space-x-2"
              title={sortOrder === 'desc' ? '최신순' : '오래된순'}
            >
              {sortOrder === 'desc' ? (
                <>
                  <FaSortAmountDown className="h-4 w-4" />
                  <span className="text-sm">최신순</span>
                </>
              ) : (
                <>
                  <FaSortAmountUp className="h-4 w-4" />
                  <span className="text-sm">오래된순</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* 통계 요약 */}
      <div className="p-6 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border-b border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* 총 수입 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">💰 총 수입</h4>
            <div className="space-y-1">
              {formatCurrencyAmount(statistics.incomeTotals).map((amount, idx) => (
                <p key={idx} className="text-lg font-bold text-green-600 dark:text-green-400">
                  {amount}
                </p>
              ))}
              {formatCurrencyAmount(statistics.incomeTotals).length === 0 && (
                <p className="text-lg font-bold text-gray-400">0 HUF</p>
              )}
            </div>
          </div>

          {/* 총 지출 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">💸 총 지출</h4>
            <div className="space-y-1">
              {formatCurrencyAmount(statistics.expenseTotals).map((amount, idx) => (
                <p key={idx} className="text-lg font-bold text-red-600 dark:text-red-400">
                  {amount}
                </p>
              ))}
              {formatCurrencyAmount(statistics.expenseTotals).length === 0 && (
                <p className="text-lg font-bold text-gray-400">0 HUF</p>
              )}
            </div>
          </div>

          {/* 순이익 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">📊 순이익</h4>
            <div className="space-y-1">
              {formatCurrencyAmount(statistics.netTotals).map((amount, idx) => {
                const value = Object.values(statistics.netTotals)[idx];
                return (
                  <p key={idx} className={`text-lg font-bold ${value >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}`}>
                    {value >= 0 ? '+' : ''}{amount}
                  </p>
                );
              })}
              {formatCurrencyAmount(statistics.netTotals).length === 0 && (
                <p className="text-lg font-bold text-gray-400">0 HUF</p>
              )}
            </div>
          </div>
        </div>

        {/* 검색 결과 정보 */}
        <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
          전체 <span className="font-bold text-purple-600 dark:text-purple-400">{pagination.totalItems}건</span> 중
          <span className="font-bold text-purple-600 dark:text-purple-400 mx-1">
            {pagination.totalItems > 0 ? (pagination.currentPage - 1) * pagination.pageSize + 1 : 0}
            ~
            {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)}건
          </span>
          표시 중
        </div>
      </div>

      {/* 차트 영역 */}
      {showCharts && !loading && filteredAndSortedRecords.length > 0 && (
        <div className="print-only">
          <AnnualFinanceCharts records={filteredAndSortedRecords} selectedYear={selectedYear} />
        </div>
      )}

      {/* 데이터 목록 */}
      <div className="p-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <FaSpinner className="h-8 w-8 animate-spin text-purple-500" />
            <span className="ml-2 text-gray-600 dark:text-gray-400">데이터 로딩 중...</span>
          </div>
        ) : filteredAndSortedRecords.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <FaChartLine className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>해당 조건의 재정 데이터가 없습니다.</p>
            <p className="text-sm mt-2">다른 필터 조건을 시도해보세요.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      날짜
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      수입
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      지출
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      순손익
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      작성자
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {pagination.paginatedData.map((record) => {
                    const { income, expense, net } = calculateFilteredTotals(record);
                    return (
                      <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium text-gray-900 dark:text-white">
                          {record.date}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-green-600 dark:text-green-400">
                          {formatCurrencyAmount(income).join(', ') || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-red-600 dark:text-red-400">
                          {formatCurrencyAmount(expense).join(', ') || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                          <div className="space-y-1">
                            {formatCurrencyAmount(net).map((amount, idx) => {
                              const currency = Object.keys(net)[idx] as keyof CurrencyTotals;
                              const value = net[currency];
                              return (
                                <div key={idx} className={value >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}>
                                  {value >= 0 ? '+' : ''}{amount}
                                </div>
                              );
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                          {record.createdBy}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* 페이지 크기 선택 */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">페이지당:</span>
                <select
                  value={pagination.pageSize}
                  onChange={(e) => pagination.setPageSize(Number(e.target.value))}
                  className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white text-sm"
                >
                  {pagination.pageSizeOptions?.map(size => (
                    <option key={size} value={size}>{size}개</option>
                  ))}
                </select>
              </div>

              {/* 페이지 네비게이션 */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={pagination.goToPrevPage}
                  disabled={!pagination.canGoPrev}
                  className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaChevronLeft className="h-4 w-4" />
                </button>

                {pagination.getPageNumbers().map((page, idx) => (
                  <button
                    key={idx}
                    onClick={() => typeof page === 'number' && pagination.setCurrentPage(page)}
                    disabled={typeof page !== 'number'}
                    className={`px-3 py-1 rounded-lg text-sm ${page === pagination.currentPage
                        ? 'bg-purple-600 text-white'
                        : typeof page === 'number'
                          ? 'border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                          : 'cursor-default'
                      }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={pagination.goToNextPage}
                  disabled={!pagination.canGoNext}
                  className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaChevronRight className="h-4 w-4" />
                </button>
              </div>

              {/* 페이지 정보 */}
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {pagination.currentPage} / {pagination.totalPages} 페이지
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
